import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import type { SiteEvent, SeverityLevel, EventType } from '../../../redux/api/eventsApi';
import { useListSiteEventsQuery } from '../../../redux/api/eventsApi';
import { SEVERITY_HEX_COLORS, EVENT_TYPES, SEVERITIES } from '../../../constants/eventStyles';

interface EventsLogModuleProps {
  siteId: string;
  onEventClick?: (eventId: string) => void;
}

type SortMode = 'latest' | 'important';

const SEVERITY_WEIGHT: Record<SeverityLevel, number> = { critical: 4, high: 3, medium: 2, low: 1 };

function formatTimeAgo(dateString: string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const hours = Math.floor((now - then) / (1000 * 60 * 60));
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

const EventsLogModule: React.FC<EventsLogModuleProps> = ({ siteId, onEventClick }) => {
  const [sortMode, setSortMode] = useState<SortMode>('latest');
  const [typeFilter, setTypeFilter] = useState<EventType | ''>('');
  const [severityFilter, setSeverityFilter] = useState<SeverityLevel | ''>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [limit, setLimit] = useState(10);

  const { data, isLoading } = useListSiteEventsQuery({
    siteId,
    limit: 100,
    event_type: typeFilter || undefined,
    severity: severityFilter || undefined,
    search: searchTerm || undefined,
    sort_by: sortMode === 'latest' ? 'occurred_at' : undefined,
    sort_dir: sortMode === 'latest' ? 'desc' : undefined,
  });

  const events = useMemo(() => {
    let items = data?.items || [];
    if (sortMode === 'important') {
      items = [...items].sort((a, b) => {
        const sa = (a.energy_impact_kwh || 0) + SEVERITY_WEIGHT[a.severity] * 100;
        const sb = (b.energy_impact_kwh || 0) + SEVERITY_WEIGHT[b.severity] * 100;
        return sb - sa;
      });
    }
    return items.slice(0, limit);
  }, [data, sortMode, limit]);

  const total = data?.total || 0;

  return (
    <div className="space-y-3">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Sort toggle */}
        <div className="flex gap-1">
          <button
            onClick={() => setSortMode('latest')}
            className="px-2.5 py-1 rounded text-xs font-medium transition-all"
            style={{
              backgroundColor: sortMode === 'latest' ? 'rgba(23, 196, 128, 0.15)' : 'transparent',
              color: sortMode === 'latest' ? '#17c480' : 'var(--color-text-muted)',
            }}
          >
            Latest
          </button>
          <button
            onClick={() => setSortMode('important')}
            className="px-2.5 py-1 rounded text-xs font-medium transition-all"
            style={{
              backgroundColor: sortMode === 'important' ? 'rgba(23, 196, 128, 0.15)' : 'transparent',
              color: sortMode === 'important' ? '#17c480' : 'var(--color-text-muted)',
            }}
          >
            Most Important
          </button>
        </div>

        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value as EventType | '')}
          className="eg-select text-xs py-1"
        >
          {EVENT_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>

        <select
          value={severityFilter}
          onChange={e => setSeverityFilter(e.target.value as SeverityLevel | '')}
          className="eg-select text-xs py-1"
        >
          {SEVERITIES.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        <div className="relative flex-1 min-w-[120px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3" style={{ color: 'var(--color-text-muted)' }} />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="eg-input text-xs py-1 pl-7 w-full"
          />
        </div>
      </div>

      {/* Trending alert (mock) */}
      <div
        className="px-3 py-2 rounded-lg text-xs"
        style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#F59E0B' }}
      >
        Trending: 3 inverter events in 6 months — escalating pattern
      </div>

      {/* Events list */}
      {isLoading ? (
        <div className="eg-loading text-sm">Loading events...</div>
      ) : events.length === 0 ? (
        <p className="text-sm py-4 text-center" style={{ color: 'var(--color-text-muted)' }}>No events found</p>
      ) : (
        <div className="space-y-2">
          {events.map(event => (
            <button
              key={event.id}
              onClick={() => onEventClick?.(event.id)}
              className="w-full text-left p-3 rounded-lg transition-colors hover:bg-[var(--color-surface-hover)]"
              style={{ border: '1px solid var(--color-border)' }}
            >
              <div className="flex items-start gap-2">
                <span
                  className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                  style={{ backgroundColor: SEVERITY_HEX_COLORS[event.severity] }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] font-semibold uppercase" style={{ color: SEVERITY_HEX_COLORS[event.severity] }}>
                      {event.severity}
                    </span>
                    <span className="text-[10px] uppercase" style={{ color: 'var(--color-text-muted)' }}>
                      {event.event_type}
                    </span>
                    <span className="text-[10px] ml-auto" style={{ color: 'var(--color-text-muted)' }}>
                      {formatTimeAgo(event.occurred_at)}
                    </span>
                  </div>
                  <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    {event.title}
                  </p>
                  {event.description && (
                    <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>
                      {event.description}
                    </p>
                  )}
                  {event.energy_impact_kwh != null && event.energy_impact_kwh > 0 && (
                    <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                      Est. loss: {(event.energy_impact_kwh / 1000).toFixed(1)} MWh · {event.status}
                    </p>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Load more */}
      {limit < total && (
        <button
          onClick={() => setLimit(prev => prev + 10)}
          className="w-full text-center py-2 text-xs font-medium text-[#17c480] hover:text-[#17c480]/80 transition-colors"
        >
          Load more...
        </button>
      )}
    </div>
  );
};

export default EventsLogModule;
